import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Loading from '../Loading';
import {IBaseElementProps} from "../BasicElement";
import cn from 'classnames';
import {createFocusTrap, Options as focusTrapOptions} from 'focus-trap';

export interface IModalI18n {
	submit: string;
	cancel: string;
}

export interface IModalProps extends IBaseElementProps {
	id?: string;
	title?: string;
	showSubmit?: boolean;
	showCancel?: boolean;
	showClose?: boolean;
	onSubmit?: (e: any) => void;
	onCancel?: (e: any) => void;
	busy?: boolean;
	i18n?: IModalI18n;
	className?: string;
	modalClassNames?: string[];
	headerClassNames?: string[];
	bodyClassNames?: string[];
	footerClassNames?: string[];
	noScrollBody?: boolean;
	showBackdrop?: boolean;
	closeOnOutsideClick?: boolean;
	closeOnEscape?: boolean;
	/**
	 * Trapping Focus is a good thing to do for modals that have form elements.  However, if there are no focussable
	 * elements in the modal, then trapping focus will cause an error.  We try for that case, so the component won't
	 * completely crash.  Still, it's something to be aware of.  You'll have to explicitly request trapFocus to turn
	 * the trapping on.
	 */
	trapFocus?: boolean;
	trapFocusOptions?: focusTrapOptions;
}
const Modal:React.FC<IModalProps> = ({
	id= 'ad-hoc-modal',
	title,
	showSubmit= false,
	showCancel = false,
	showClose = true,
	onSubmit = () => {},
	onCancel = () => {},
	busy,
	i18n= {
		submit: 'Submit',
		cancel: 'Cancel'
	} as IModalI18n,
	children,
	className,
	modalClassNames= ['modal-lg','modal-dialog-centered'],
	headerClassNames = ['ps-5','pt-4'],
	bodyClassNames = ['py-2','px-5'],
	footerClassNames= ['pe-5','pb-4'],
	noScrollBody= true,
	showBackdrop = true,
	closeOnOutsideClick = true,
	closeOnEscape = true,
	trapFocus = true,
	trapFocusOptions,
}) => {
	useEffect( () => {
		if ( noScrollBody ) {
			document.body.classList.add('overflow-hidden');
			return () => {
				document.body.classList.remove('overflow-hidden');
			}
		}
	}, [noScrollBody] );

	useEffect( () => {
		if ( closeOnOutsideClick ) {
			const listener = (e:any) => {
				// the .modal div is the backdrop, so if clicking on that, then close modal
				if ( e.target.classList.contains('modal') ) {
					onCancel(e);
				}
			}
			document.addEventListener( 'click', listener );
			return () => {
				document.removeEventListener( 'click', listener );
			}
		}
	}, [closeOnOutsideClick,onCancel] );

	useEffect( () => {
		if ( closeOnEscape ) {
			const listener = (e:any) => {
				if ( 27 === e.which && ! e.defaultPrevented ) {
					onCancel(e);
				}
			}
			document.addEventListener( 'keyup', listener );
			return () => {
				document.removeEventListener( 'keyup', listener );
			}
		}
	}, [closeOnEscape,onCancel] );

	const ref = useRef<HTMLDivElement>(null);
	useEffect( () => {
		if ( trapFocus && ref.current ) {
			const trap = createFocusTrap(ref.current,trapFocusOptions);
			try {
				trap.activate();
				return () => {
					trap.deactivate();
				}
			} catch ( e ) {
				// Unable to trap focus.  Most likely explanation is that there are no focusable elements in the modal.
			}
		}
	}, [ref.current,trapFocus,trapFocusOptions]);

	return ReactDOM.createPortal(<>
		<div ref={ref} className={`modal fade show ${className||''}`} style={{display:'block'}} id={id} aria-labelledby={id + 'label'} aria-hidden="false" tabIndex={-1}>
			<div className={cn('modal-dialog',modalClassNames)}>
				<div className="modal-content" style={{overflow:'visible'}}>
					<div className={cn('modal-header',headerClassNames)}>
						{title && <h5 className="modal-title" id={id + 'label'}>{title}</h5>}
						{ showClose && <button type="button" className="btn-close" onClick={onCancel} aria-label={i18n.cancel} /> }
					</div>
					<div className={cn('modal-body',bodyClassNames)}>
						{children}
					</div>
					{ ( showCancel || showSubmit ) && <div className={cn('modal-footer',footerClassNames)}>
						{ busy && <Loading inline/> }
						{ showCancel && <button type="button" disabled={busy} className="btn btn-secondary" onClick={onCancel}>{i18n.cancel}</button> }
						{ showSubmit && <button type="button" disabled={busy} className="btn btn-primary" onClick={onSubmit}>{i18n.submit}</button> }
					</div>}
				</div>
			</div>
		</div>
		{ showBackdrop && <div className="modal-backdrop fade show"/>}
	</>, document.body);
}

export default Modal;
